interface ApiErrorResponse {
  status?: string;
  message?: string;
  error?: string;
  metadata?: Record<string, unknown>;
  issues?: Array< { path: string; message: string } >;
  validation?: {
    body?: { message?: string };
    query?: { message?: string };
    params?: { message?: string };
    keys?: string[];
    source?: string;
  };
}

interface AxiosErrorLike {
  response?: {
    status?: number;
    data?: ApiErrorResponse;
  };
  message?: string;
  code?: string;
}

const MENSAGENS_PADRAO: Record<number, string> = {
  400: 'Dados inválidos. Verifique as informações e tente novamente.',
  401: 'Sessão expirada ou não autorizada. Faça login novamente.',
  403: 'Você não tem permissão para esta ação.',
  404: 'Recurso não encontrado.',
  500: 'Erro interno no servidor. Tente novamente mais tarde.',
};

function labelDoCampo(campo: string): string {
  const labels: Record<string, string> = {
    email: 'E-mail',
    password: 'Senha',
    name: 'Nome',
    document: 'CPF',
    document_type: 'Tipo de documento',
    phone: 'Telefone',
    recaptcha: 'Verificação de segurança',
    cpf: 'CPF',
    amount: 'Valor',
    description: 'Descrição',
  };
  return labels[campo] || campo;
}

/**
 * Retorna uma única mensagem legível a partir da resposta da API.
 */
export function getApiErrorMessage(error: AxiosErrorLike): string {
  const status = error?.response?.status;
  const data = error?.response?.data as ApiErrorResponse | undefined;
  const msgGenérica = error?.message || '';

  // Erro de rede (sem response)
  if (!error?.response) {
    if (error?.code === 'NETWORK_ERROR' || msgGenérica?.toLowerCase().includes('network')) {
      return 'Erro de conexão. Verifique sua internet e tente novamente.';
    }
    return msgGenérica || 'Ocorreu um erro. Tente novamente.';
  }

  const message = data?.message;
  const statusType = data?.status;

  // Validação Zod (issues com path e message)
  if (statusType === 'validationError' && Array.isArray(data?.issues) && data.issues.length > 0) {
    const partes = data.issues.map(
      (i) => `${labelDoCampo(i.path) || i.path}: ${i.message}`
    );
    return partes.join('. ');
  }

  // AppError (backend) – pode ter metadata com detalhes
  if (statusType === 'appError' && message) {
    if (data?.metadata && typeof data.metadata === 'object') {
      const extra = Object.entries(data.metadata)
        .map(([k, v]) => `${labelDoCampo(k)}: ${String(v)}`)
        .join('. ');
      return extra ? `${message}. ${extra}` : message;
    }
    return message;
  }

  // Celebrate/Joi – message genérica "Validation failed" + validation
  if (status === 400 && (message === 'Validation failed' || message?.toLowerCase().includes('validation'))) {
    const validation = data?.validation;
    if (validation?.body?.message) return validation.body.message;
    if (validation?.query?.message) return validation.query.message;
    if (validation?.params?.message) return validation.params.message;
    if (Array.isArray(validation?.keys) && validation.keys.length > 0) {
      const campos = validation.keys.map(labelDoCampo).join(', ');
      return `Verifique os campos: ${campos}.`;
    }
    return 'Dados inválidos. Verifique os campos e tente novamente.';
  }

  // Mensagem explícita da API (em inglês ou português)
  if (message && typeof message === 'string' && message.trim().length > 0) {
    // Não mostrar a mensagem genérica do Axios
    if (msgGenérica === 'Request failed with status code 400' || msgGenérica.startsWith('Request failed with status code')) {
      return message;
    }
    return message;
  }

  // Fallback por status
  if (status && MENSAGENS_PADRAO[status]) {
    return MENSAGENS_PADRAO[status];
  }

  return MENSAGENS_PADRAO[400] || 'Ocorreu um erro. Tente novamente.';
}
